import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Fetch original through the authorized JFM API using the same auth as the
// photo list. Never put private storage keys into the browser URL or logs.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export default function ProjectOriginalPhotoPreview({ projectId, photo }) {
  const [expanded, setExpanded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [preview, setPreview] = useState({ state: "loading", url: "", error: "" });

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setExpanded(false);
    setPreview({ state: "loading", url: "", error: "" });
    axios.get(`${API}/projects/${projectId}/photos/${photo.id}/original-preview`,
      { responseType: "blob" })
      .then(({ data }) => {
        if (!(data instanceof Blob) || !data.type.startsWith("image/")) {
          throw new Error("Original image preview returned an invalid image");
        }
        objectUrl = URL.createObjectURL(data);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreview({ state: "ready", url: objectUrl, error: "" });
      })
      .catch(async error => {
        if (cancelled) return;
        let detail = "Original photo not accessible. Showing available thumbnail.";
        const data = error?.response?.data;
        if (data instanceof Blob && data.type.includes("json")) {
          try {
            const parsed = JSON.parse(await data.text());
            if (typeof parsed.detail === "string") detail = parsed.detail;
          } catch { /* Default message remains. */ }
        } else if (data?.detail) {
          detail = data.detail;
        }
        if (!cancelled) setPreview({ state: "error", url: "", error: detail });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [projectId, photo.id, attempt]);

  return (
    <div data-testid="project-photo-original-preview" className="photo-gps-original-preview">
      <img
        src={preview.url || photo.thumbnail_url}
        alt={photo.original_filename}
        data-testid="project-photo-visible-image"
        className="photo-gps-original-preview__image"
      />
      {preview.state === "loading" && <small>Loading the original photo…</small>}
      {preview.state === "error" && <div><small role="alert">{preview.error}</small>
        <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry preview</button>
      </div>}
      {preview.state === "ready" && (
        <div>
          <small>{["image/heic", "image/heif"].includes(photo.content_type)
            ? "Full-resolution display copy loaded. Original file is unchanged."
            : "Original photo loaded from JFM storage."}</small>
          <a href={preview.url} onClick={(event) => { event.preventDefault(); setExpanded(true); }}
            data-testid="project-photo-expand-original">Expand image</a>
        </div>
      )}
      <Dialog open={expanded && preview.state === "ready"} onOpenChange={setExpanded}>
        <DialogContent data-testid="project-photo-expanded-original" aria-describedby={undefined}
          overlayProps={{ style: { zIndex: 79 } }}
          style={{ zIndex: 80, maxWidth: "calc(100vw - 1rem)", maxHeight: "calc(100dvh - 1rem)", overflow: "auto" }}>
          <DialogHeader><DialogTitle>{photo.original_filename}</DialogTitle></DialogHeader>
          <img src={preview.url} alt={photo.original_filename}
            style={{ width: "100%", maxHeight: "80dvh", objectFit: "contain" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
